import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  BarChart3, PieChart, Globe, TrendingUp, Users, CheckCircle,
  Clock, FileText, ArrowUpRight, ArrowDownRight, Loader2, RefreshCw
} from 'lucide-react';
import { universityAPI } from '../../utils/api';

const UniversityAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');
  const [university, setUniversity] = useState(null);

  useEffect(() => {
    const storedUniversity = localStorage.getItem('university');
    if (storedUniversity) {
      setUniversity(JSON.parse(storedUniversity));
    }
  }, []);

  useEffect(() => {
    if (university) {
      fetchAnalytics();
    }
  }, [university, period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const data = await universityAPI.getAnalytics(university._id, period);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    draft: 'bg-gray-100 text-gray-700',
    submitted: 'bg-blue-100 text-blue-700',
    under_review: 'bg-yellow-100 text-yellow-700',
    accepted: 'bg-green-100 text-green-700',
    denied: 'bg-red-100 text-red-700',
    waitlisted: 'bg-purple-100 text-purple-700',
    deferred: 'bg-orange-100 text-orange-700',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <BarChart3 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">No Analytics Data</h3>
        <p className="text-gray-500">Analytics will appear once you receive applications.</p>
      </div>
    );
  }

  const { application_trends, status_distribution, geographic_distribution, gpa_distribution, response_metrics } = analytics;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Analytics Dashboard</h2>
          <p className="text-gray-600">Track application trends and metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['7d', '30d', '90d', '1y'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  period === p
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : p === '90d' ? '90 Days' : '1 Year'}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchAnalytics}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Applications</p>
                <p className="text-3xl font-bold">{response_metrics.total_applications}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Review</p>
                <p className="text-3xl font-bold">{response_metrics.pending_review}</p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Decisions Made</p>
                <p className="text-3xl font-bold">{response_metrics.decisions_made}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Response Rate</p>
                <p className="text-3xl font-bold">{response_metrics.response_rate}%</p>
              </div>
              <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Application Trends */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Application Trends
            </CardTitle>
            <CardDescription>Weekly application volume</CardDescription>
          </CardHeader>
          <CardContent>
            {application_trends.length > 0 ? (
              <div className="space-y-3">
                {application_trends.slice(-8).map((trend, index) => {
                  const maxCount = Math.max(...application_trends.map(t => t.count));
                  const percentage = (trend.count / maxCount) * 100;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-20">{trend.week}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-blue-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">{trend.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No trend data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-purple-600" />
              Status Distribution
            </CardTitle>
            <CardDescription>Applications by current status</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(status_distribution).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(status_distribution).map(([status, count]) => {
                  const total = Object.values(status_distribution).reduce((a, b) => a + b, 0);
                  const percentage = ((count / total) * 100).toFixed(1);
                  return (
                    <div key={status} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[status] || 'bg-gray-100'}>
                          {status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-purple-600 h-full rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-600 w-16 text-right">
                          {count} ({percentage}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No status data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Second Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Geographic Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-emerald-600" />
              Geographic Distribution
            </CardTitle>
            <CardDescription>Applicants by country</CardDescription>
          </CardHeader>
          <CardContent>
            {geographic_distribution.length > 0 ? (
              <div className="space-y-3">
                {geographic_distribution.map((geo, index) => {
                  const maxCount = Math.max(...geographic_distribution.map(g => g.count));
                  const percentage = (geo.count / maxCount) * 100;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm w-24 truncate">{geo.country || 'Unknown'}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">{geo.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No geographic data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* GPA Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              GPA Distribution
            </CardTitle>
            <CardDescription>Applicants by GPA range</CardDescription>
          </CardHeader>
          <CardContent>
            {gpa_distribution.length > 0 ? (
              <div className="space-y-3">
                {gpa_distribution.map((gpa, index) => {
                  const total = gpa_distribution.reduce((a, b) => a + b.count, 0);
                  const percentage = total > 0 ? ((gpa.count / total) * 100).toFixed(1) : 0;
                  const colors = [
                    'from-green-500 to-emerald-500',
                    'from-blue-500 to-cyan-500',
                    'from-yellow-500 to-orange-500',
                    'from-orange-500 to-red-500',
                    'from-red-500 to-pink-500',
                  ];
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-20">{gpa.range}</span>
                      <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                        <div 
                          className={`bg-gradient-to-r ${colors[index]} h-full rounded-full`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-16 text-right">
                        {gpa.count} ({percentage}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No GPA data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UniversityAnalytics;
